import React from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    IconButton,
    Button,
    Box,
    Typography,
    LinearProgress,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import Transition from '../common/Transition';

interface ConfirmDialogProps {
    open: boolean;
    title: string;
    bodyText: string;
    isLoading: boolean;
    onClose: () => void;
    onConfirm: () => void;
    cancelButtonText: string;
    confirmButtonText: string;
    confirmColor?: 'primary' | 'error';
}

const ConfirmDialog: React.FC<ConfirmDialogProps> = ({
    open,
    title,
    bodyText,
    isLoading,
    onClose,
    onConfirm,
    cancelButtonText,
    confirmButtonText,
    confirmColor = 'primary',
}) => {
    const handleClose = (_: React.SyntheticEvent, reason?: string) => {
        if (reason !== 'backdropClick') {
            onClose();
        }
    };

    return (
        <Dialog
            open={open}
            TransitionComponent={Transition}
            keepMounted
            onClose={handleClose}
            fullWidth
            maxWidth="sm"
            aria-labelledby="confirm-dialog-title"
            aria-describedby="confirm-dialog-description"
            PaperProps={{
                sx: {
                    borderRadius: 3,
                    overflow: 'hidden',
                    border: '1px solid var(--selected-table-row-color)',
                    backgroundColor: 'background.paper',
                },
            }}
        >
            <DialogTitle
                id="confirm-dialog-title"
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    px: 3,
                    py: 2,
                    background: 'linear-gradient(135deg, var(--card-header-background-color) 0%, var(--button-background-color) 100%)',
                    borderBottom: '1px solid var(--selected-table-row-color)',
                }}
            >
                <Typography
                    variant="h6"
                    component="span"
                    sx={{
                        fontWeight: 700,
                        color: 'white',
                        lineHeight: 1.3,
                    }}
                >
                    {title}
                </Typography>
                <IconButton
                    onClick={onClose}
                    size="small"
                    sx={{
                        backgroundColor: 'white',
                        color: 'var(--link-text-color)',
                        border: '1px solid var(--selected-table-row-color)',
                        '&:hover': {
                            borderColor: 'var(--button-background-color)',
                            backgroundColor: 'white',
                        },
                    }}
                >
                    <CloseIcon fontSize="small" />
                </IconButton>
            </DialogTitle>

            <DialogContent
                sx={{
                    px: 3,
                    py: 2.75,
                    minHeight: 88,
                    display: 'flex',
                    alignItems: 'center',
                }}
            >
                <Typography
                    variant="body1"
                    id="confirm-dialog-description"
                    sx={{
                        color: 'text.secondary',
                        lineHeight: 1.55,
                    }}
                >
                    {bodyText}
                </Typography>
            </DialogContent>

            {isLoading ? (
                <Box sx={{ px: 3, pb: 2.5 }}>
                    <LinearProgress
                        sx={{
                            height: 4,
                            borderRadius: 2,
                            backgroundColor: 'var(--selected-table-row-color)',
                            '& .MuiLinearProgress-bar': {
                                backgroundColor: 'var(--button-background-color)',
                                borderRadius: 2,
                            },
                        }}
                    />
                </Box>
            ) : (
                <DialogActions
                    sx={{
                        px: 3,
                        pb: 2.5,
                        pt: 2,
                        gap: 1.25,
                        borderTop: '1px solid var(--selected-table-row-color)',
                        backgroundColor: 'action.hover',
                    }}
                >
                    <Button
                        onClick={onClose}
                        variant="outlined"
                        size="medium"
                        sx={{
                            minWidth: 108,
                            minHeight: 40,
                            borderRadius: 2,
                            textTransform: 'none',
                            fontWeight: 600,
                            borderColor: 'var(--selected-table-row-color)',
                            color: 'var(--link-text-color)',
                            '&:hover': {
                                borderColor: 'var(--button-background-color)',
                                backgroundColor: 'action.hover',
                            },
                        }}
                    >
                        {cancelButtonText}
                    </Button>
                    {confirmColor === 'error' ? (
                        <Button
                            onClick={onConfirm}
                            variant="outlined"
                            color="error"
                            size="medium"
                            sx={{
                                minWidth: 128,
                                minHeight: 40,
                                borderRadius: 2,
                                textTransform: 'none',
                                fontWeight: 600,
                                borderWidth: 1.5,
                            }}
                        >
                            {confirmButtonText}
                        </Button>
                    ) : (
                        <Button
                            onClick={onConfirm}
                            variant="contained"
                            size="medium"
                            className="button-color"
                            sx={{
                                minWidth: 128,
                                minHeight: 40,
                                borderRadius: 2,
                                textTransform: 'none',
                                fontWeight: 700,
                            }}
                        >
                            {confirmButtonText}
                        </Button>
                    )}
                </DialogActions>
            )}
        </Dialog>
    );
};

export default ConfirmDialog;
